import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaBullhorn, FaMapMarkerAlt, FaHistory, FaCheckCircle, FaVideo, FaSearch, FaClock, FaDesktop, FaExternalLinkAlt, FaTimesCircle } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReporterRaiseVoiceTabs from "./ReporterRaiseVoiceTabs";

const ReporterGetRunningVoice = () => {
  const [voices, setVoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const totalPages = Math.ceil(voices.length / rowsPerPage);

  useEffect(() => {
    const fetchVoices = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/reporter/get/running/ryv/proofs`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("get running voice data", response);
        setVoices(Array.isArray(response.data.data) ? response.data.data : []);
      } catch (err) {
        if (err.response && err.response.status === 404 && err.response.data.message.includes("No running")) {
          setVoices([]);
          setError(null);
        } else {
          setError("Failed to fetch running voices");
          toast.error("Failed to fetch running voices");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchVoices();
  }, []);

  const paginatedVoices = voices.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 py-2 sm:py-6 px-2 sm:px-6">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Modern Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto mb-6"
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-700 via-indigo-700 to-indigo-900 rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 text-white">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-400/10 rounded-full blur-xl -ml-5 -mb-5"></div>

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="p-1.5 bg-white/20 backdrop-blur-md rounded-lg shadow-inner">
                  <FaHistory className="text-lg sm:text-xl text-blue-200" />
                </div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-black tracking-tight leading-none uppercase italic">
                  Submission Logs
                </h1>
              </div>
              <p className="text-blue-100 text-[10px] sm:text-sm font-medium max-w-xl opacity-80">
                Tracking <span className="font-black text-white px-1.5 py-0.5 bg-white/10 rounded-md">{voices.length}</span> proofs currently under review or broadcasting.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <ReporterRaiseVoiceTabs />

      {error && (
        <div className="max-w-7xl mx-auto bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-3">
          <FaTimesCircle className="shrink-0" />
          <p className="font-medium text-sm">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="max-w-7xl mx-auto">
          {voices.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paginatedVoices.map((voice, idx) => (
                <motion.div
                  key={voice._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-2xl border border-slate-100 shadow-md hover:shadow-xl transition-all duration-300 flex overflow-hidden h-36 sm:h-40 group border-l-4 border-l-indigo-500"
                >
                  {/* Compact Media - Left Side */}
                  <div className="relative w-28 sm:w-44 h-full bg-slate-900 shrink-0 overflow-hidden">
                    {voice.adId?.image ? (
                      <img
                        src={voice.adId.image.startsWith("http") ? voice.adId.image : `${process.env.REACT_APP_API_BASE_URL}/${voice.adId.image.replace(/\\/g, "/")}`}
                        alt="RYV"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : voice.adId?.video ? (
                      <video
                        src={voice.adId.video.startsWith("http") ? voice.adId.video : `${process.env.REACT_APP_API_BASE_URL}/${voice.adId.video.replace(/\\/g, "/")}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-100">
                        <FaBullhorn className="text-slate-200 text-2xl" />
                      </div>
                    )}

                    {/* Floating Badge */}
                    <div className="absolute bottom-2 left-2 flex gap-1">
                      <span className={`px-1.5 py-0.5 rounded-md text-[7px] font-black uppercase text-white ${voice.proof ? 'bg-indigo-500' : 'bg-amber-500'}`}>
                        {voice.proof ? 'Running' : 'Pending'}
                      </span>
                    </div>
                  </div>

                  {/* Dense Content - Right Side */}
                  <div className="p-3 flex-1 flex flex-col min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <h3 className="text-slate-900 font-extrabold text-sm sm:text-base leading-tight line-clamp-1 group-hover:text-indigo-600 transition-colors">
                        {voice.adId?.description || 'Broadcast Submission'}
                      </h3>
                      <span className="text-[8px] font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100 shrink-0">
                        ID: {(voice.iinsafId || voice._id).slice(-6)}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[9px] font-bold text-slate-400 uppercase mt-0.5">
                      <span className="flex items-center gap-1"><FaDesktop className="text-indigo-400 shrink-0" /> {voice.platform}</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-slate-300 shrink-0"></span>
                      <span className="flex items-center gap-1 font-mono text-[8px] tracking-tighter"><FaClock className="text-slate-300" /> {voice.duration}</span>
                    </div>

                    <div className="flex items-center gap-2 text-[9px] font-bold text-slate-400 mt-2">
                      <FaHistory className="text-slate-300 shrink-0" />
                      <span>Submitted: {new Date(voice.submittedAt).toLocaleDateString('en-GB')} {new Date(voice.submittedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>

                    <div className="mt-auto flex items-center gap-2 pt-2 border-t border-slate-50">
                      {voice.videoLink ? (
                        <a
                          href={voice.videoLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white transition-all font-black text-[9px] uppercase border border-indigo-100"
                        >
                          <FaExternalLinkAlt size={8} /> View Link
                        </a>
                      ) : (
                        <button disabled className="flex-1 py-1.5 rounded-lg bg-slate-50 text-slate-300 font-black text-[9px] uppercase">
                          No Link
                        </button>
                      )}

                      <div className="px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center gap-1.5">
                        <FaCheckCircle className="text-emerald-500" size={10} />
                        <span className="text-emerald-700 font-black text-[9px] uppercase">Proof Sent</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-[2rem] border border-slate-100 p-12 text-center"
            >
              <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaBullhorn className="text-slate-200 text-4xl" />
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">No Running Voices</h3>
              <p className="text-slate-500 max-w-xs mx-auto text-sm">You haven't submitted any proofs for broadcasted voices yet. Complete assignments to see them here.</p>
            </motion.div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-10 mb-10">
              <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-slate-100 shadow-sm">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${currentPage === 1 ? 'text-slate-200' : 'text-indigo-600 hover:bg-indigo-50 active:scale-90'}`}
                >
                  ←
                </button>
                <div className="flex items-center px-4 gap-1">
                  <span className="text-xs font-black text-slate-800 uppercase tracking-widest">Page</span>
                  <span className="w-8 h-8 flex items-center justify-center bg-indigo-600 text-white rounded-lg text-xs font-black">{currentPage}</span>
                  <span className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">of</span>
                  <span className="text-xs font-black text-slate-800 uppercase tracking-widest">{totalPages}</span>
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${currentPage === totalPages ? 'text-slate-200' : 'text-indigo-600 hover:bg-indigo-50 active:scale-90'}`}
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReporterGetRunningVoice;
